import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import ContactForm from '../Components/ContactForm'; // Import the ContactForm component
import RentForm from '../Components/RentForm'; // Import the RentForm component
import ForwardForm from '../Components/ForwardForm'; // Import the ForwardForm component
import { getSiteBrandName, setPageTitle } from '../Utils/brand';
import { extractHashtags, stripHashtags } from '../Utils/hashtag';
import { DomainCardItem } from './Home/SearchDOMAINS';
import {
    BuyIcon,
    RentIcon,
    ForwardIcon,
    CheckCircleIcon,
    ArrowLeftIcon,
    WarningIcon,
    LockIcon,
    AlertCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '../Components/Icons';

const DomainPageSkeleton = () => (
    <main className="pt-20 min-h-screen animate-fade-simple">
        <div className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl xl:max-w-6xl mx-auto space-y-10">
                {/* Shimmering Breadcrumb */}
                <div className="flex items-center space-x-2 text-xs sm:text-sm">
                    <div className="w-16 h-4 skeleton-shimmer rounded-md" />
                    <span className="text-neutral-600">/</span>
                    <div className="w-28 h-4 skeleton-shimmer rounded-md" />
                </div>

                {/* Grand Domain Card Skeleton */}
                <div className="relative rounded-3xl p-8 sm:p-12 md:p-16 overflow-hidden bg-gradient-to-b from-neutral-900/85 via-neutral-900/60 to-neutral-950/90 backdrop-blur-3xl border border-white/[0.14] shadow-[inset_0_1px_1px_rgba(255,255,255,0.22),inset_0_-1px_1px_rgba(255,255,255,0.03),0_25px_60px_rgba(0,0,0,0.7)]">
                    <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none" />

                    {/* Header */}
                    <div className="text-center relative z-10 mb-8 sm:mb-12 flex flex-col items-center">
                        {/* Status Pill Skeleton */}
                        <div className="w-28 h-7 skeleton-shimmer rounded-full mb-4" />

                        {/* Title Skeleton */}
                        <div className="w-64 sm:w-96 md:w-[480px] h-12 sm:h-16 skeleton-shimmer rounded-2xl mb-4" />

                        {/* Neon line placeholder */}
                        <div className="w-48 sm:w-72 h-3.5 skeleton-shimmer rounded-full mb-3 opacity-60" />

                        {/* Description Skeleton */}
                        <div className="w-full max-w-lg h-4 skeleton-shimmer rounded-md mb-2" />
                        <div className="w-3/4 max-w-md h-4 skeleton-shimmer rounded-md mb-6" />

                        {/* Tag Pills */}
                        <div className="flex justify-center gap-2">
                            <div className="w-20 h-6 skeleton-shimmer rounded-full" />
                            <div className="w-24 h-6 skeleton-shimmer rounded-full" />
                        </div>
                    </div>

                    {/* 3 Pricing Boxes Skeleton */}
                    <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto gap-3 sm:gap-4 md:gap-5 w-full">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="p-4 sm:p-5 md:p-6 rounded-2xl bg-gradient-to-b from-neutral-900/80 via-neutral-900/50 to-neutral-950/80 border border-white/[0.12] flex flex-col items-center text-center space-y-3">
                                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl skeleton-shimmer" />
                                <div className="w-20 h-3 skeleton-shimmer rounded" />
                                <div className="w-28 h-7 skeleton-shimmer rounded-lg" />
                                <div className="w-24 h-3 skeleton-shimmer rounded" />
                                <div className="w-full h-8 skeleton-shimmer rounded-xl mt-3" />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Navigation Buttons Skeleton Directly Below Card */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-1">
                    <div className="h-10 w-44 rounded-xl bg-white/[0.04] skeleton-shimmer" />
                    <div className="h-4 w-56 rounded bg-white/[0.03] skeleton-shimmer" />
                </div>

                {/* Carousel of 3 Skeleton Cards (without buttons & without borders) */}
                <div className="pt-6 space-y-4">
                    <div className="space-y-2 px-1">
                        <div className="h-6 w-64 rounded-lg bg-white/[0.06] skeleton-shimmer" />
                        <div className="h-3.5 w-80 rounded bg-white/[0.03] skeleton-shimmer" />
                    </div>

                    <div className="grid grid-cols-1 min-[800px]:grid-cols-2 min-[1100px]:grid-cols-3 gap-4">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="rounded-3xl border-0 bg-neutral-900/40 p-6 space-y-4 shadow-xl overflow-hidden relative"
                            >
                                <div className="flex items-start justify-between relative z-10">
                                    <div className="space-y-2">
                                        <div className="h-6 w-36 rounded-lg bg-white/[0.06] skeleton-shimmer" />
                                        <div className="h-1 w-12 rounded-full bg-white/[0.04]" />
                                    </div>
                                    <div className="h-5 w-14 rounded-full bg-white/[0.04] skeleton-shimmer" />
                                </div>
                                <div className="space-y-2 pt-1 relative z-10">
                                    <div className="h-3.5 w-full rounded bg-white/[0.03] skeleton-shimmer" />
                                    <div className="h-3.5 w-2/3 rounded bg-white/[0.03] skeleton-shimmer" />
                                </div>
                                <div className="flex gap-1.5 pt-1 relative z-10">
                                    <div className="h-5 w-14 rounded-md bg-white/[0.03] skeleton-shimmer" />
                                    <div className="h-5 w-16 rounded-md bg-white/[0.03] skeleton-shimmer" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
        <Footer />
    </main>
);

export default function DomainPage() {
    const { domainName: rawDomainName } = useParams();
    // Security: sanitize domainName against XSS, path traversal, and malicious script injection
    const domainName = useMemo(() => {
        let clean = (rawDomainName || '').replace(/[^a-zA-Z0-9.-]/g, '').slice(0, 100);
        if (clean.toLowerCase().endsWith('.html')) {
            clean = clean.slice(0, -5);
        }
        return clean;
    }, [rawDomainName]);
    const [domain, setDomain] = useState(null);
    const [otherDomains, setOtherDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeModalTab, setActiveModalTab] = useState('forward'); // 'buy' | 'rent' | 'forward'
    const loadAttemptRef = useRef(0);
    const maxLoadAttempts = 2; // 1 initial request + 1 single automatic retry on failure
    const isMountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    // Carousel state and controls
    const carouselRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [isCarouselPaused, setIsCarouselPaused] = useState(false);

    const checkCarouselScroll = useCallback(() => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 10);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    }, []);

    const scrollAnimationRef = useRef(null);

    const smoothScrollTo = useCallback((targetLeft, duration = 650) => {
        if (!carouselRef.current) return;
        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
        }

        const element = carouselRef.current;
        const startLeft = element.scrollLeft;
        const maxScroll = element.scrollWidth - element.clientWidth;
        const clampedTarget = Math.max(0, Math.min(targetLeft, maxScroll));
        const distance = clampedTarget - startLeft;

        if (Math.abs(distance) < 2) return;

        const startTime = performance.now();

        // Quintic out / Apple-like physics ease-out
        const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);

        const animateScroll = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOutQuint(progress);

            element.scrollLeft = startLeft + distance * easedProgress;

            if (progress < 1) {
                scrollAnimationRef.current = requestAnimationFrame(animateScroll);
            } else {
                element.scrollLeft = clampedTarget;
                checkCarouselScroll();
            }
        };

        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    }, [checkCarouselScroll]);

    const scrollCarousel = (direction) => {
        if (carouselRef.current) {
            const element = carouselRef.current;
            const cardElement = element.querySelector('.snap-start');
            const cardWidth = cardElement ? (cardElement.offsetWidth + 16) : (element.clientWidth * 0.75);
            const moveAmount = element.clientWidth > 768 ? cardWidth * 2 : cardWidth;
            const targetLeft = direction === 'left' 
                ? element.scrollLeft - moveAmount 
                : element.scrollLeft + moveAmount;
            
            // Temporarily relax scroll snap so the browser smoothly glides without instantly snapping
            element.style.scrollSnapType = 'none';
            smoothScrollTo(targetLeft, 600);
            setTimeout(() => {
                if (carouselRef.current) {
                    carouselRef.current.style.scrollSnapType = 'x mandatory';
                }
            }, 650);
        }
    };

    // Smooth auto-scroll for "Explore Other Premium Domains" (pauses on hover or touch)
    useEffect(() => {
        if (otherDomains.length === 0 || isCarouselPaused) return;

        const interval = setInterval(() => {
            if (carouselRef.current) {
                const element = carouselRef.current;
                const { scrollLeft, scrollWidth, clientWidth } = element;
                const cardElement = element.querySelector('.snap-start');
                const cardWidth = cardElement ? (cardElement.offsetWidth + 16) : clientWidth;

                if (scrollLeft + clientWidth >= scrollWidth - 20) {
                    smoothScrollTo(0, 900);
                } else {
                    smoothScrollTo(scrollLeft + cardWidth, 750);
                }
            }
        }, 4500);

        return () => {
            clearInterval(interval);
            if (scrollAnimationRef.current) {
                cancelAnimationFrame(scrollAnimationRef.current);
            }
        };
    }, [otherDomains.length, isCarouselPaused, smoothScrollTo]);

    // Interactive 3D Cursor & Touch Tilt for the Grand Domain Titanium Plate (follows cursor across screen)
    const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, shineX: 50, shineY: 50, isHovered: true });
    const [isLowerCardsHovered, setIsLowerCardsHovered] = useState(false);
    const tiltCardRef = useRef(null);
    const isModalOpenRef = useRef(false);
    isModalOpenRef.current = isModalOpen;
    const isLowerCardsHoveredRef = useRef(false);
    isLowerCardsHoveredRef.current = isLowerCardsHovered;

    const handlePointerMove = useCallback((clientX, clientY) => {
        // When any modal, overlay, menu or drawer is open on the page, freeze calculations
        if (isModalOpenRef.current) return;
        if (document.querySelector('.fixed.inset-0:not(.hidden), [role="dialog"], .modal-open, .navbar-menu-open')) return;

        // When user is interacting with lower cards / carousel, pause calculations
        if (isLowerCardsHoveredRef.current) return;
        if (!tiltCardRef.current) return;
        const rect = tiltCardRef.current.getBoundingClientRect();

        // If pointer is far below the grand card (more than 180px under its bottom edge), pause tracking
        if (clientY > rect.bottom + 180) return;

        const cardCenterX = rect.left + rect.width / 2;
        const cardCenterY = rect.top + rect.height / 2;

        // Relative offset from card center, smoothly clamped
        const offsetX = (clientX - cardCenterX) / (window.innerWidth / 2);
        const offsetY = (clientY - cardCenterY) / (window.innerHeight / 2);

        const clampedX = Math.max(-1, Math.min(1, offsetX));
        const clampedY = Math.max(-1, Math.min(1, offsetY));

        // Gentle, subtle tilt: max 5 degrees for an expensive solid titanium slab feel
        const maxTilt = 5;
        const rotateX = -clampedY * maxTilt;
        const rotateY = clampedX * maxTilt;

        // Illumination coordinates relative to card surface, clamped between 5% and 95%
        const shineX = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
        const shineY = Math.max(5, Math.min(95, ((clientY - rect.top) / rect.height) * 100));

        setTilt({
            rotateX,
            rotateY,
            shineX,
            shineY,
            isHovered: true
        });
    }, []);

    // Listen to global pointer and touch movements so the card follows the cursor even outside its boundaries
    useEffect(() => {
        let rafId = null;
        const onMouseMove = (e) => {
            if (isModalOpenRef.current || isLowerCardsHoveredRef.current) return;
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => handlePointerMove(e.clientX, e.clientY));
        };
        const onTouchMove = (e) => {
            if (isModalOpenRef.current || isLowerCardsHoveredRef.current) return;
            if (e.touches && e.touches[0]) {
                if (rafId) cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(() => handlePointerMove(e.touches[0].clientX, e.touches[0].clientY));
            }
        };

        window.addEventListener('mousemove', onMouseMove, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
        };
    }, [handlePointerMove]);

    // Fetch up to 24 random catalog domains to display in carousel without overloading
    useEffect(() => {
        let isCancelled = false;
        fetch('/api/domains')
            .then(res => res.json())
            .then(data => {
                if (!isCancelled && Array.isArray(data)) {
                    const others = data.filter(d => 
                        d.name?.toLowerCase() !== domainName?.toLowerCase() && 
                        (d.status === 1 || d.status === undefined) && 
                        !(d.is_sold > 0)
                    );
                    // 24 random domains
                    const shuffled = [...others].sort(() => 0.5 - Math.random()).slice(0, 24);
                    setOtherDomains(shuffled);
                    setTimeout(checkCarouselScroll, 200);
                }
            })
            .catch(err => {
                console.warn('Failed to load other domains:', err);
            });
        return () => {
            isCancelled = true;
        };
    }, [domainName, checkCarouselScroll]);

    // Cleanup function to prevent state updates on unmounted component
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);

    const loadDomain = useCallback(async () => {
        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }

        // Prevent infinite loading attempts
        if (loadAttemptRef.current >= maxLoadAttempts) {
            if (isMountedRef.current) {
                setError('load_error');
                setLoading(false);
            }
            return;
        }

        loadAttemptRef.current += 1;

        try {
            if (isMountedRef.current) {
                setLoading(true);
            }

            // Add a small delay to prevent overwhelming the server
            await new Promise(resolve => setTimeout(resolve, 50 * loadAttemptRef.current));

            // Fetch domain data from the database API instead of JSON file
            const response = await fetch(`/api/domain/${domainName}`, {
                cache: 'no-store', // Prevent caching issues
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Domain not found');
                }
                throw new Error(`Failed to load domain: ${response.status} ${response.statusText}`);
            }

            const domainData = await response.json();

            if (!isMountedRef.current) return;

            if (domainData.status === 0 || domainData.status === -1 || domainData.status !== 1) {
                setError('unavailable');
                // Only set domain if it exists to prevent null access
                if (domainData && isMountedRef.current) {
                    setDomain(domainData);
                }
            } else if (domainData.is_sold > 0) {
                setError('sold');
                if (domainData && isMountedRef.current) {
                    setDomain(domainData);
                }
            } else {
                // Safety check before setting domain
                if (domainData && isMountedRef.current) {
                    setDomain(domainData);
                    setError(null);
                }
            }
        } catch (err) {
            console.error('Error loading domain:', err);
            // Retry on network errors
            if (isMountedRef.current && loadAttemptRef.current < maxLoadAttempts && err.message !== 'Domain not found') {
                // Wait a bit before retrying with exponential backoff
                const delay = Math.pow(2, loadAttemptRef.current) * 1000; // Increased base delay
                retryTimeoutRef.current = setTimeout(loadDomain, delay);
                return;
            }
            if (isMountedRef.current) {
                if (err.message === 'Domain not found') {
                    setError('not_found');
                } else {
                    setError('load_error');
                }
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [domainName]);

    useEffect(() => {
        // Reset attempt counter when domainName changes
        loadAttemptRef.current = 0;
        isMountedRef.current = true;
        loadDomain();



        // Cleanup function
        return () => {
            isMountedRef.current = false;

            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [loadDomain]);

    // Dynamic page title for the active domain card
    useEffect(() => {
        const brand = getSiteBrandName();
        const activeName = domain?.name || domainName;

        if (domain?.name && !error) {
            document.title = `${domain.name} - Buy or Rent Domain | ${brand}`;
        } else if (error === 'not_found') {
            document.title = `${activeName} - Domain Not Found | ${brand}`;
        } else if (error === 'unavailable') {
            document.title = `${activeName} - Domain Unavailable | ${brand}`;
        } else if (error === 'sold') {
            document.title = `${activeName} - Domain Sold | ${brand}`;
        } else if (activeName) {
            document.title = `${activeName} | ${brand}`;
        }

        return () => {
            setPageTitle(); // Reset back to default marketplace title on leave
        };
    }, [domain, domainName, error]);

    useEffect(() => {
        if (domain) {
            const buyOk = Boolean(domain.pricebuy && Number(domain.pricebuy) > 0 && !(domain.is_sold > 0));
            const rentOk = Boolean(domain.pricerent && Number(domain.pricerent) > 0 && !(domain.is_sold > 0));
            const fwOk = Boolean(domain.pricefw && Number(domain.pricefw) > 0 && !(domain.is_sold > 0));
            if (buyOk) {
                setActiveModalTab('buy');
            } else if (rentOk) {
                setActiveModalTab('rent');
            } else if (fwOk) {
                setActiveModalTab('forward');
            }
        }
    }, [domain]);

    // Loading state with zero layout shift
    if (loading) {
        return <DomainPageSkeleton />;
    }

    // Error states
    if (error === 'not_found') {
        return (
            <main className="pt-20 min-h-screen">
                <div className="py-10 px-4 sm:px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto">
                        <div className="relative rounded-3xl p-8 sm:p-10 text-center space-y-5 bg-neutral-950/60 backdrop-blur-2xl border border-white/[0.08] shadow-[inset_0_1px_1px_rgba(255,255,255,0.12),0_20px_50px_rgba(0,0,0,0.6)]">
                            <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-red-500/20">
                                <WarningIcon className="w-7 h-7 text-white" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">Domain Not Found</h1>
                            <p className="text-neutral-400 text-base sm:text-lg">
                                The domain <span className="text-primary-400 font-semibold">{domainName}</span> was not found in our marketplace.
                            </p>
                            <Link to="/" className="btn btn-primary px-6 py-2.5 text-sm font-semibold inline-block">
                                <span className="relative z-10">Back to Domains</span>
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    if (error === 'unavailable') {
        const isInactive = domain?.status === -1;
        return (
            <main className="pt-20 min-h-screen">
                <div className="py-10 px-4 sm:px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto">
                        <div className="relative rounded-3xl p-8 sm:p-10 text-center space-y-5 bg-neutral-950/60 backdrop-blur-2xl border border-white/[0.08] shadow-[inset_0_1px_1px_rgba(255,255,255,0.12),0_20px_50px_rgba(0,0,0,0.6)]">
                            <div className="w-14 h-14 bg-gradient-to-r from-amber-600 to-amber-700 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20">
                                <LockIcon className="w-7 h-7 text-white" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">
                                {isInactive ? 'Domain Inactive' : 'Domain Temporarily Unavailable'}
                            </h1>
                            <p className="text-neutral-400 text-base sm:text-lg">
                                The domain <span className="text-primary-400 font-semibold">{domain?.name || domainName}</span> is currently {isInactive ? 'inactive' : 'unavailable'}. Please check back later.
                            </p>
                            <p className="text-sm text-neutral-500">
                                {domain?.description || ''}
                            </p>
                            <Link to="/" className="btn btn-primary px-6 py-2.5 text-sm font-semibold inline-block">
                                <span className="relative z-10">Back to Domains</span>
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    if (error === 'sold') {
        return (
            <main className="pt-20 min-h-screen">
                <div className="py-10 px-4 sm:px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto">
                        <div className="relative rounded-3xl p-8 sm:p-10 text-center space-y-5 bg-neutral-950/60 backdrop-blur-2xl border border-white/[0.08] shadow-[inset_0_1px_1px_rgba(255,255,255,0.12),0_20px_50px_rgba(0,0,0,0.6)]">
                            <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-red-800 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-red-500/20">
                                <CheckCircleIcon className="w-7 h-7 text-white" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">Domain Sold</h1>
                            <p className="text-neutral-400 text-base sm:text-lg">
                                The domain <span className="text-primary-400 font-semibold">{domain?.name || domainName}</span> has been sold.
                            </p>
                            <p className="text-sm text-neutral-500">
                                {domain?.description || ''}
                            </p>
                            <Link to="/" className="btn btn-primary px-6 py-2.5 text-sm font-semibold inline-block">
                                <span className="relative z-10">Back to Domains</span>
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    if (error === 'load_error') {
        return (
            <main className="pt-20 min-h-screen">
                <div className="py-12 px-4 sm:px-6 lg:px-12">
                    <div className="max-w-xl mx-auto">
                        <div className="relative rounded-3xl p-8 sm:p-10 text-center space-y-5 bg-gradient-to-b from-neutral-900/90 via-neutral-950/90 to-dark-100 border border-white/[0.12] shadow-2xl backdrop-blur-2xl">
                            <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/10">
                                <AlertCircleIcon className="w-7 h-7" />
                            </div>
                            <div className="space-y-2">
                                <h1 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">Unable to Load Domain</h1>
                                <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
                                    Please check your connection and try again.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3 justify-center pt-2">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="btn btn-primary px-6 py-2.5 text-sm font-semibold rounded-xl"
                                >
                                    <span className="relative z-10">Try Again</span>
                                </button>
                                <Link to="/" className="btn btn-ghost px-5 py-2.5 text-sm font-semibold rounded-xl">
                                    Back to Domains
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    // Safety check: if we're not loading and don't have an error, but domain is still null
    if (!domain && error !== 'not_found' && error !== 'unavailable' && error !== 'load_error') {
        return (
            <main className="pt-20 min-h-screen">
                <div className="py-12 px-4 sm:px-6 lg:px-12">
                    <div className="max-w-xl mx-auto">
                        <div className="relative rounded-3xl p-8 sm:p-10 text-center space-y-5 bg-gradient-to-b from-neutral-900/90 via-neutral-950/90 to-dark-100 border border-white/[0.12] shadow-2xl backdrop-blur-2xl">
                            <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/10">
                                <AlertCircleIcon className="w-7 h-7" />
                            </div>
                            <div className="space-y-2">
                                <h1 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">Unable to Load Domain</h1>
                                <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
                                    Please check your connection and try again.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3 justify-center pt-2">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="btn btn-primary px-6 py-2.5 text-sm font-semibold rounded-xl"
                                >
                                    <span className="relative z-10">Try Again</span>
                                </button>
                                <Link to="/" className="btn btn-ghost px-5 py-2.5 text-sm font-semibold rounded-xl">
                                    Back to Domains
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    // Success state - show domain details
    const now = new Date();
    const rentUntil = domain?.rent_until ? new Date(domain.rent_until) : null;
    const forwardUntil = domain?.forward_until ? new Date(domain.forward_until) : null;

    let busyStatus = null;
    if (domain?.is_sold > 0) {
        busyStatus = { type: 'sold', label: 'Sold', color: 'bg-red-500/20 text-red-400 border-red-500/30' };
    }
    else if (rentUntil && rentUntil > now) {
        busyStatus = { type: 'rented', label: `Busy until ${rentUntil.toLocaleDateString()}`, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
    }
    else if (forwardUntil && forwardUntil > now) {
        busyStatus = { type: 'forwarded', label: `Busy until ${forwardUntil.toLocaleDateString()}`, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
    }

    const hasBuy = Boolean(domain?.pricebuy && Number(domain.pricebuy) > 0 && !(domain?.is_sold > 0));
    const hasRent = Boolean(domain?.pricerent && Number(domain.pricerent) > 0 && !(domain?.is_sold > 0));
    const hasForward = Boolean(domain?.pricefw && Number(domain.pricefw) > 0 && !(domain?.is_sold > 0));
    const enabledCount = [hasBuy, hasRent, hasForward].filter(Boolean).length;

    return (
        <main className="pt-20 min-h-screen">
            <div className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl xl:max-w-6xl mx-auto space-y-10">
                    {/* Breadcrumb */}
                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-neutral-400">
                        <Link to="/" className="hover:text-primary-400 transition-colors">Domains</Link>
                        <ChevronRightIcon className="w-3.5 h-3.5 text-neutral-500" />
                        <span className="text-white font-medium">{domain?.name || domainName}</span>
                    </div>

                    {/* Domain Card Container with 3D Perspective */}
                    <div 
                        className="relative w-full"
                        style={{ perspective: '1200px' }}
                    >
                        <div 
                            ref={tiltCardRef}
                            className="relative rounded-3xl p-8 sm:p-12 md:p-16 overflow-hidden metallic-card domain-grand-card no-hover will-change-transform"
                            style={{
                                '--light-x': `${tilt.shineX}%`,
                                '--light-y': `${tilt.shineY}%`,
                                '--light-opacity': tilt.isHovered ? '1' : '0',
                                transform: `rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) ${tilt.isHovered ? 'translateY(-4px)' : 'translateY(0px)'}`,
                                transformStyle: 'preserve-3d',
                                boxShadow: tilt.isHovered 
                                    ? 'inset 0 -1px 2px 0 rgba(0, 0, 0, 0.8), 0 28px 60px -12px rgba(0, 0, 0, 0.85), 0 0 35px -6px rgba(255, 255, 255, 0.05)'
                                    : 'inset 0 -1px 1px 0 rgba(0, 0, 0, 0.7), 0 14px 36px -10px rgba(0, 0, 0, 0.7)',
                                transition: tilt.isHovered ? 'transform 0.12s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.25s ease' : 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease'
                            }}
                        >
                            {/* Flashlight Border Sheen: illuminates card border directly beneath the light cursor */}
                            <div 
                                className="absolute inset-0 pointer-events-none rounded-3xl z-20 transition-opacity duration-300"
                                style={{
                                    opacity: tilt.isHovered ? 1 : 0,
                                    padding: '1px',
                                    background: `radial-gradient(circle 380px at ${tilt.shineX}% ${tilt.shineY}%, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0.15) 35%, transparent 70%)`,
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude'
                                }}
                            />

                            {/* Dynamic Specular Light Reflection that glides across the titanium plate according to cursor */}
                            <div 
                                className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                                style={{
                                    opacity: tilt.isHovered ? 0.35 : 0,
                                    background: `radial-gradient(circle 500px at ${tilt.shineX}% ${tilt.shineY}%, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.05) 45%, transparent 70%)`
                                }}
                            />

                            {/* Brushed metallic hairlines subtle texture */}
                            <div className="absolute inset-0 metallic-hairlines opacity-40 pointer-events-none" />

                            {/* Ambient dynamic theme refraction */}
                            <div className="absolute -top-36 -right-36 w-96 h-96 rounded-full blur-3xl pointer-events-none opacity-40"
                                 style={{ background: 'radial-gradient(circle, var(--theme-glow-rgba, rgba(14,165,233,0.3)) 0%, transparent 70%)' }} />
                            <div className="absolute -bottom-36 -left-36 w-96 h-96 rounded-full blur-3xl pointer-events-none opacity-30"
                                 style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)' }} />
                            <div className="absolute inset-x-0 top-0 metallic-top-sheen pointer-events-none" />

                        {/* Header */}
                        <div className="text-center relative z-10 mb-8 sm:mb-12">
                            {(() => {
                                if (busyStatus) {
                                    return (
                                        <div className="flex justify-center mb-4">
                                            <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide border shadow-sm ${busyStatus.color}`}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
                                                <span>{busyStatus.label}</span>
                                            </span>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="flex justify-center mb-4">
                                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.09] border border-white/15 shadow-[0_0_15px_rgba(255,255,255,0.08),inset_0_1px_1px_rgba(255,255,255,0.2)] backdrop-blur-md transition-all">
                                            <span className="relative flex h-2 w-2">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                                            </span>
                                            <span className="text-xs font-medium text-white/90 tracking-wide">
                                                Available
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="relative inline-block max-w-full">
                                <h1 
                                    className="text-[clamp(2rem,6.5vw,4.5rem)] domain-hero-title mb-4 whitespace-nowrap select-all"
                                    style={{
                                        '--light-x': `${tilt.shineX}%`,
                                        '--light-y': `${tilt.shineY}%`
                                    }}
                                >
                                    {domain?.name || domainName}
                                </h1>
                            </div>


                            <p className="text-base sm:text-lg text-neutral-300 max-w-2xl mx-auto leading-relaxed font-normal mt-2">
                                {stripHashtags(domain?.description) || domain?.description || 'Domain information not available.'}
                            </p>

                            {extractHashtags(domain?.description).length > 0 && (
                                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                                    {extractHashtags(domain?.description).map((tag, tIdx) => (
                                        <Link
                                            key={tIdx}
                                            to={`/?q=%23${tag}#domains`}
                                            className="px-3 py-1 rounded-full text-xs font-medium bg-neutral-900/80 hover:bg-primary-500/20 text-primary-300 hover:text-white border border-white/10 hover:border-primary-500/40 transition-all hover:scale-105 backdrop-blur-sm"
                                        >
                                            #{tag}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Pricing Section (Adaptive: 3 in a row >= 800px, compact column < 800px) */}
                        <div className={`relative z-10 grid gap-2.5 sm:gap-3 md:gap-4 w-full ${
                            enabledCount === 1 
                                ? 'grid-cols-1 max-w-sm mx-auto' 
                                : enabledCount === 2 
                                    ? 'grid-cols-1 min-[800px]:grid-cols-2 max-w-sm min-[800px]:max-w-xl mx-auto' 
                                    : 'grid-cols-1 min-[800px]:grid-cols-3 max-w-sm min-[800px]:max-w-4xl mx-auto'
                        }`}>
                            {/* Buy Option */}
                            {hasBuy && (
                                <div 
                                    onClick={!(domain?.is_sold > 0) ? () => {
                                        setActiveModalTab('buy');
                                        setIsModalOpen(true);
                                    } : undefined}
                                    role={!(domain?.is_sold > 0) ? "button" : undefined}
                                    tabIndex={!(domain?.is_sold > 0) ? 0 : undefined}
                                    onKeyDown={!(domain?.is_sold > 0) ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setActiveModalTab('buy');
                                            setIsModalOpen(true);
                                        }
                                    } : undefined}
                                    className={`relative overflow-hidden text-center p-3.5 sm:p-4 md:p-5 rounded-2xl metallic-card flex flex-col justify-between group/buy ${
                                        !(domain?.is_sold > 0)
                                            ? 'cursor-pointer hover:-translate-y-1 active:scale-[0.98]'
                                            : 'opacity-60 cursor-not-allowed'
                                    }`}
                                >
                                    <div className="absolute inset-0 metallic-hairlines opacity-35 pointer-events-none" />
                                    <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-2xl pointer-events-none opacity-20 group-hover/buy:opacity-40 transition-opacity duration-300 bg-white/[0.08]" />
                                    
                                    <div className="relative z-10">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 metallic-slot rounded-xl flex items-center justify-center mx-auto mb-2.5 sm:mb-3 group-hover/buy:border-white/25 group-hover/buy:scale-105 transition-all duration-300">
                                            <BuyIcon className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-400 group-hover/buy:text-white group-hover/buy:animate-cart-fill transition-all duration-200" />
                                        </div>
                                        <div>
                                            <p className="text-neutral-400 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">Buy price</p>
                                            {domain?.discount_buy > 0 ? (
                                                <div className="flex flex-col items-center">
                                                    <p className="text-xl sm:text-2xl md:text-3xl font-extrabold font-mono text-white transition-colors break-words max-w-full">
                                                        ${(domain.pricebuy * (1 - domain.discount_buy / 100)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                    </p>
                                                    <p className="text-[11px] sm:text-xs font-mono line-through text-neutral-500 mt-0.5">
                                                        ${Number(domain.pricebuy).toLocaleString()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <p className="text-xl sm:text-2xl md:text-3xl font-extrabold font-mono text-white transition-colors break-words max-w-full">
                                                    ${Number(domain.pricebuy).toLocaleString()}
                                                </p>
                                            )}
                                            <p className="text-[11px] sm:text-xs text-neutral-400 mt-1">One-time payment</p>
                                        </div>
                                    </div>

                                    {!(domain?.is_sold > 0) && (
                                        <div className="relative z-10 mt-4 sm:mt-5 pt-3 border-t border-white/[0.08] flex items-center justify-center gap-1.5 text-[11px] sm:text-xs font-semibold text-neutral-400 group-hover/buy:text-white transition-colors">
                                            <span>Select Buy Option</span>
                                            <span className="transition-transform duration-200 group-hover/buy:translate-x-1">→</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Rent Option */}
                            {hasRent && (
                                <div 
                                    onClick={!(domain?.is_sold > 0) ? () => {
                                        setActiveModalTab('rent');
                                        setIsModalOpen(true);
                                    } : undefined}
                                    role={!(domain?.is_sold > 0) ? "button" : undefined}
                                    tabIndex={!(domain?.is_sold > 0) ? 0 : undefined}
                                    onKeyDown={!(domain?.is_sold > 0) ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setActiveModalTab('rent');
                                            setIsModalOpen(true);
                                        }
                                    } : undefined}
                                    className={`relative overflow-hidden text-center p-3.5 sm:p-4 md:p-5 rounded-2xl metallic-card flex flex-col justify-between group/rent ${
                                        !(domain?.is_sold > 0)
                                            ? 'cursor-pointer hover:-translate-y-1 active:scale-[0.98]'
                                            : 'opacity-60 cursor-not-allowed'
                                    }`}
                                >
                                    <div className="absolute inset-0 metallic-hairlines opacity-35 pointer-events-none" />
                                    <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-2xl pointer-events-none opacity-20 group-hover/rent:opacity-40 transition-opacity duration-300 bg-white/[0.08]" />
                                    
                                    <div className="relative z-10">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 metallic-slot rounded-xl flex items-center justify-center mx-auto mb-2.5 sm:mb-3 group-hover/rent:border-white/25 group-hover/rent:scale-105 transition-all duration-300">
                                            <RentIcon className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-400 group-hover/rent:text-white group-hover/rent:animate-clock-fast transition-all duration-200" />
                                        </div>
                                        <div>
                                            <p className="text-neutral-400 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">Rent price</p>
                                            {domain?.discount_rent > 0 ? (
                                                <div className="flex flex-col items-center">
                                                    <p className="text-xl sm:text-2xl md:text-3xl font-extrabold font-mono text-white transition-colors break-words max-w-full">
                                                        ${(domain.pricerent * (1 - domain.discount_rent / 100)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                    </p>
                                                    <p className="text-[11px] sm:text-xs font-mono line-through text-neutral-500 mt-0.5">
                                                        ${Number(domain.pricerent).toLocaleString()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <p className="text-xl sm:text-2xl md:text-3xl font-extrabold font-mono text-white transition-colors break-words max-w-full">
                                                    ${Number(domain.pricerent).toLocaleString()}
                                                </p>
                                            )}
                                            <p className="text-[11px] sm:text-xs text-neutral-400 mt-1">Per month</p>
                                        </div>
                                    </div>

                                    {!(domain?.is_sold > 0) && (
                                        <div className="relative z-10 mt-4 sm:mt-5 pt-3 border-t border-white/[0.08] flex items-center justify-center gap-1.5 text-[11px] sm:text-xs font-semibold text-neutral-400 group-hover/rent:text-white transition-colors">
                                            <span>Select Rent Option</span>
                                            <span className="transition-transform duration-200 group-hover/rent:translate-x-1">→</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Forward Option */}
                            {hasForward && (
                                <div 
                                    onClick={!(domain?.is_sold > 0) ? () => {
                                        setActiveModalTab('forward');
                                        setIsModalOpen(true);
                                    } : undefined}
                                    role={!(domain?.is_sold > 0) ? "button" : undefined}
                                    tabIndex={!(domain?.is_sold > 0) ? 0 : undefined}
                                    onKeyDown={!(domain?.is_sold > 0) ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setActiveModalTab('forward');
                                            setIsModalOpen(true);
                                        }
                                    } : undefined}
                                    className={`relative overflow-hidden text-center p-3.5 sm:p-4 md:p-5 rounded-2xl metallic-card flex flex-col justify-between group/fw ${
                                        !(domain?.is_sold > 0)
                                            ? 'cursor-pointer hover:-translate-y-1 active:scale-[0.98]'
                                            : 'opacity-60 cursor-not-allowed'
                                    }`}
                                >
                                    <div className="absolute inset-0 metallic-hairlines opacity-35 pointer-events-none" />
                                    <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-2xl pointer-events-none opacity-20 group-hover/fw:opacity-40 transition-opacity duration-300 bg-white/[0.08]" />
                                    
                                    <div className="relative z-10">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 metallic-slot rounded-xl flex items-center justify-center mx-auto mb-2.5 sm:mb-3 group-hover/fw:border-white/25 group-hover/fw:scale-105 transition-all duration-300">
                                            <ForwardIcon className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-400 group-hover/fw:text-white group-hover/fw:animate-bounce-x transition-all duration-200" />
                                        </div>
                                        <div>
                                            <p className="text-neutral-400 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">Forward Price</p>
                                            {domain?.discount_fw > 0 ? (
                                                <div className="flex flex-col items-center">
                                                    <p className="text-xl sm:text-2xl md:text-3xl font-extrabold font-mono text-white transition-colors break-words max-w-full">
                                                        ${(domain.pricefw * (1 - domain.discount_fw / 100)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                                    </p>
                                                    <p className="text-[11px] sm:text-xs font-mono line-through text-neutral-500 mt-0.5">
                                                        ${Number(domain.pricefw).toLocaleString()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <p className="text-xl sm:text-2xl md:text-3xl font-extrabold font-mono text-white transition-colors break-words max-w-full">
                                                    ${Number(domain.pricefw).toLocaleString()}
                                                </p>
                                            )}
                                            <p className="text-[11px] sm:text-xs text-neutral-400 mt-1">Per day</p>
                                        </div>
                                    </div>

                                    {!(domain?.is_sold > 0) && (
                                        <div className="relative z-10 mt-4 sm:mt-5 pt-3 border-t border-white/[0.08] flex items-center justify-center gap-1.5 text-[11px] sm:text-xs font-semibold text-neutral-400 group-hover/fw:text-white transition-colors">
                                            <span>Select Forward Option</span>
                                            <span className="transition-transform duration-200 group-hover/fw:translate-x-1">→</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    </div>

                    {/* Streamlined Navigation Links Directly Below Card */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm px-1">
                        <Link to="/" className="px-5 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-neutral-300 hover:text-white font-medium inline-flex items-center gap-2 transition-all">
                            <ArrowLeftIcon className="w-4 h-4" />
                            <span>Back to All Domains</span>
                        </Link>
                        <p className="text-neutral-400">
                            Have questions? Check our <Link to="/faq.html" className="text-primary-400 hover:text-primary-300 underline underline-offset-4">FAQ page</Link>.
                        </p>
                    </div>

                    {/* Explore Other Premium Domains (Carousel with 24 Random Domains) */}
                    {otherDomains.length > 0 && (
                        <div className="pt-6 space-y-4">
                            <div className="px-1 mb-1">
                                <Link
                                    to="/#domains"
                                    className="group/title inline-flex flex-col items-start transition-all duration-300"
                                    title="View all domains in catalog"
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-xl sm:text-2xl font-bold font-display text-white group-hover/title:text-primary-300 transition-colors flex items-center gap-2">
                                            <span>Explore Other <span className="bg-gradient-to-r from-sky-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">Premium Domains</span></span>
                                        </h3>
                                    </div>
                                    <p className="text-xs sm:text-sm text-neutral-400 group-hover/title:text-neutral-300 transition-colors">
                                        Exclusive marketplace domains available for immediate transfer or lease
                                    </p>
                                </Link>
                            </div>

                            {/* Carousel Slider with Edge Navigation and Responsive Whole-Card Fit */}
                            <div className="relative group/carousel flex items-center gap-3 sm:gap-6 px-1 sm:px-3">
                                {/* Flanking Left Navigation Button (no background box, subtle icon only) */}
                                <button
                                    type="button"
                                    onClick={() => scrollCarousel('left')}
                                    disabled={!canScrollLeft}
                                    aria-label="Previous domains"
                                    className={`flex-none w-8 sm:w-10 h-16 flex items-center justify-center transition-all ${
                                        canScrollLeft
                                            ? 'text-neutral-400 hover:text-white active:scale-90 cursor-pointer opacity-80 hover:opacity-100 hover:scale-110'
                                            : 'text-neutral-600 opacity-25 cursor-not-allowed pointer-events-none'
                                    }`}
                                >
                                    <ChevronLeftIcon className="w-6 h-6 sm:w-7 sm:h-7" />
                                </button>

                                {/* Clipped Viewport Area */}
                                <div className="flex-1 overflow-hidden min-w-0">
                                    <div 
                                        ref={carouselRef}
                                        onScroll={checkCarouselScroll}
                                        onMouseEnter={() => {
                                            setIsCarouselPaused(true);
                                            setIsLowerCardsHovered(true);
                                        }}
                                        onMouseLeave={() => {
                                            setIsCarouselPaused(false);
                                            setIsLowerCardsHovered(false);
                                        }}
                                        onTouchStart={() => {
                                            setIsCarouselPaused(true);
                                            setIsLowerCardsHovered(true);
                                        }}
                                        onTouchEnd={() => {
                                            setIsCarouselPaused(false);
                                            setIsLowerCardsHovered(false);
                                        }}
                                        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-none py-4 px-0 select-none"
                                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory' }}
                                    >
                                        {otherDomains.map((other) => (
                                            <div key={other.id} className="flex-none w-full min-[800px]:w-[calc(50%-8px)] min-[1100px]:w-[calc(33.333%-11px)] snap-start snap-always px-2">
                                            <DomainCardItem
                                                item={other}
                                                compact={true}
                                                setShowContactForm={() => {
                                                    setDomain(other);
                                                    setActiveModalTab('buy');
                                                    setIsModalOpen(true);
                                                }}
                                                setShowRentForm={() => {
                                                    setDomain(other);
                                                    setActiveModalTab('rent');
                                                    setIsModalOpen(true);
                                                }}
                                                setShowForwardForm={() => {
                                                    setDomain(other);
                                                    setActiveModalTab('forward');
                                                    setIsModalOpen(true);
                                                }}
                                                setSelectedDomain={setDomain}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                                {/* Flanking Right Navigation Button (no background box, subtle icon only) */}
                                <button
                                    type="button"
                                    onClick={() => scrollCarousel('right')}
                                    disabled={!canScrollRight}
                                    aria-label="Next domains"
                                    className={`flex-none w-8 sm:w-10 h-16 flex items-center justify-center transition-all ${
                                        canScrollRight
                                            ? 'text-neutral-400 hover:text-white active:scale-90 cursor-pointer opacity-80 hover:opacity-100 hover:scale-110'
                                            : 'text-neutral-600 opacity-25 cursor-not-allowed pointer-events-none'
                                    }`}
                                >
                                    <ChevronRightIcon className="w-6 h-6 sm:w-7 sm:h-7" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />

            {/* Domain Action Modals (Kept mounted to preserve state/payment view across tab switches and close/reopen) */}
            {domain && (
                <div
                    className={isModalOpen ? "fixed inset-0 bg-black/85 backdrop-blur-xl flex items-center justify-center p-2.5 sm:p-6 z-50 overflow-y-auto modal-scrollbar animate-in fade-in duration-200" : "hidden"}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setIsModalOpen(false);
                        }
                    }}
                >
                    {hasBuy && (
                        <div className={activeModalTab === 'buy' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
                            <ContactForm
                                domain={domain}
                                onClose={() => setIsModalOpen(false)}
                                isPurchase={true}
                                onSwitchToRent={hasRent ? () => setActiveModalTab('rent') : undefined}
                                onSwitchToForward={hasForward ? () => setActiveModalTab('forward') : undefined}
                            />
                        </div>
                    )}

                    {hasRent && (
                        <div className={activeModalTab === 'rent' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
                            <RentForm
                                domain={domain}
                                onClose={() => setIsModalOpen(false)}
                                onSwitchToBuy={hasBuy ? () => setActiveModalTab('buy') : undefined}
                                onSwitchToForward={hasForward ? () => setActiveModalTab('forward') : undefined}
                            />
                        </div>
                    )}

                    {hasForward && (
                        <div className={activeModalTab === 'forward' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
                            <ForwardForm
                                domain={domain}
                                onClose={() => setIsModalOpen(false)}
                                onSwitchToRent={hasRent ? () => setActiveModalTab('rent') : undefined}
                                onSwitchToBuy={hasBuy ? () => setActiveModalTab('buy') : undefined}
                            />
                        </div>
                    )}
                </div>
            )}
        </main>
    );
}
